/**
 * @fileoverview Cached API actions for Click&Pledge (web).
 * Fetches and caches responses in Redux for the session.
 */

import CryptoJS from 'crypto-js';

import { fetchAxiosApi } from '../../../../base/mtApi/functions';
import {
    setClickandPledgeApiCache
} from '../../../actions';

import {
    FORM_SETTINGS_API,
    GET_FORM_API,
    GET_FORM_BY_PARAMS_API,
    SUBMIT_SOAP_API
} from './constants';

const getCache = (getState, cacheKey) =>
    getState()['features/clickandpledge']?.apiCache?.[cacheKey];

const setCache = (dispatch, cacheKey, data) => {
    dispatch(setClickandPledgeApiCache(cacheKey, data));
};

// Tracks in-flight requests by cache key so concurrent callers share a single
// network round-trip. The Redux cache only populates AFTER a request resolves,
// so without this two callers racing the same key (e.g. `preloadFormData` and
// the donation form's own fetch both requesting `/form`) each miss the cache and
// fire a duplicate request. The promise is removed once it settles, so failed
// requests are retried on the next call rather than being permanently shared.
const _inFlightByKey = new Map();

const dedupeInFlight = (cacheKey, requestFn) => {
    const existing = _inFlightByKey.get(cacheKey);

    if (existing) {
        return existing;
    }

    const promise = (async () => {
        try {
            return await requestFn();
        } finally {
            _inFlightByKey.delete(cacheKey);
        }
    })();

    _inFlightByKey.set(cacheKey, promise);

    return promise;
};

const fetchFormByParamsCached = async ({
    dispatch,
    getState,
    cacheKey,
    giventId,
    guid,
    param
}) => {
    const cached = getCache(getState, cacheKey);

    if (cached) {
        return cached;
    }

    return dedupeInFlight(cacheKey, async () => {
        const params = { guid: `${giventId}/${guid}?${param}=1` };
        const response = await fetchAxiosApi(`${GET_FORM_BY_PARAMS_API}/form`, {
            method: 'post',
            body: { body: params }
        });

        if (!response?.body) {
            return null;
        }

        const parsed = JSON.parse(JSON.parse(response.body));

        setCache(dispatch, cacheKey, parsed);

        return parsed;
    });
};

export const fetchFormSettingsCached = ({ guid, giventId }) => async (dispatch, getState) => {
    if (!guid || !giventId) {
        return null;
    }

    const cacheKey = `formSettings:${guid}:${giventId}`;
    const cached = getCache(getState, cacheKey);

    if (cached) {
        return cached;
    }

    return dedupeInFlight(cacheKey, async () => {
        const response = await fetchAxiosApi(`${FORM_SETTINGS_API}/formsettings`, {
            method: 'post',
            body: {
                body: {
                    guid,
                    giventId
                }
            }
        });

        if (!response?.body) {
            return null;
        }

        const formsettings = JSON.parse(response.body);

        setCache(dispatch, cacheKey, formsettings);

        return formsettings;
    });
};

export const submitRegistrationCached = ({ postbody }) => async (dispatch, getState) => {
    const cacheKey = `registration:${postbody.HostID}:${postbody.MeetingID}:${postbody.GiventToken}`;
    const cached = getCache(getState, cacheKey);

    if (cached) {
        return cached;
    }

    return dedupeInFlight(cacheKey, async () => {
        const postRegBody = CryptoJS.AES.encrypt(
            JSON.stringify({ body: { postbody } }),
            'gatheringjar$@0047'
        ).toString();
        const response = await fetchAxiosApi(`${GET_FORM_API}/registration`, {
            method: 'post',
            body: {
                httpMethod: 'POST',
                path: '/registration',
                postRegBody
            }
        });

        setCache(dispatch, cacheKey, response);

        return response;
    });
};

export const fetchFormWithTokenCached = ({ accountId, giventToken, streamformattedData }) => async (dispatch, getState) => {
    const token = giventToken?.trim() || '';
    const cacheKey = `getform:${accountId}:${token}:${streamformattedData}`;
    const cached = getCache(getState, cacheKey);

    if (cached) {
        return cached;
    }

    return dedupeInFlight(cacheKey, async () => {
        const authtokenbody = { params: `${accountId}/${token}/${streamformattedData}` };
        const postBody = CryptoJS.AES.encrypt(
            JSON.stringify({ body: { authtokenbody } }),
            'gatheringjar$@0047'
        ).toString();

        const response = await fetchAxiosApi(`${GET_FORM_API}/getform`, {
            method: 'post',
            body: {
                httpMethod: 'POST',
                path: '/getform',
                postBody
            }
        });

        if (!response?.body) {
            return null;
        }

        const parsed = JSON.parse(response.body);

        setCache(dispatch, cacheKey, parsed);

        return parsed;
    });
};

export const fetchConvenienceFeeCached = ({ guid, giventId }) => (dispatch, getState) =>
    fetchFormByParamsCached({
        dispatch,
        getState,
        cacheKey: `convenienceFee:${guid}:${giventId}`,
        giventId,
        guid,
        param: 'cf'
    });

export const fetchRecurringDataCached = ({ guid, giventId }) => (dispatch, getState) =>
    fetchFormByParamsCached({
        dispatch,
        getState,
        cacheKey: `recurring:${guid}:${giventId}`,
        giventId,
        guid,
        param: 'rec'
    });

export const fetchPaymentDetailsCached = ({ guid, giventId }) => (dispatch, getState) =>
    fetchFormByParamsCached({
        dispatch,
        getState,
        cacheKey: `payment:${guid}:${giventId}`,
        giventId,
        guid,
        param: 'pd'
    });


/**
 * Fetches campaign raised data directly from API without caching.
 * Always retrieves fresh data from the server.
 */
export const fetchCampaignRaisedFresh = ({ organizationId, campaignId }) => async () => {
    if (!organizationId || !campaignId) {
        return null;
    }

    const postbody = { params: `${organizationId}/${campaignId}` };
    const postCampBody = CryptoJS.AES.encrypt(
        JSON.stringify({ body: { postbody } }),
        'gatheringjar$@0047'
    ).toString();
    const response = await fetchAxiosApi(`${GET_FORM_API}/campaignRaised`, {
        method: 'post',
        body: {
            httpMethod: 'POST',
            path: '/campaignRaised',
            postCampBody
        }
    });

    if (!response?.body) {
        return null;
    }

    const forms = JSON.parse(response.body).forms;

    return forms;
};

export const submitDonation = ({ xmldata }) => async () => {
    const postXMLBody = CryptoJS.AES.encrypt(
        JSON.stringify({ body: { xmldata } }),
        'gatheringjar$@0047'
    ).toString();
    const response = await fetchAxiosApi(`${SUBMIT_SOAP_API}/donation`, {
        method: 'post',
        body: {
            httpMethod: 'POST',
            path: '/',
            postXMLBody
        }
    });

    return response?.body;
};

export const postAmazonPaymentIntent = ({ url, payload }) => async () => {
    const response = await fetchAxiosApi(url, {
        method: 'post',
        body: payload,
        headers: { 'Content-Type': 'application/json' }
    });

    return response;
};

export const postAmazonGateway = ({ url, payload }) => async () => {
    const response = await fetchAxiosApi(url, {
        method: 'post',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'text'
    });

    return response;
};

export const postStripePaymentIntent = ({ url, payload }) => async () => {
    const response = await fetchAxiosApi(url, {
        method: 'post',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'text'
    });

    return response;
};

export const postPayPalRecurringToken = ({ url, payload }) => async () => {
    const response = await fetchAxiosApi(url, {
        method: 'post',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'text'
    });

    return response;
};

export const postPayPalCreateOrder = ({ url, payload }) => async () => {
    const response = await fetchAxiosApi(url, {
        method: 'post',
        body: payload,
        headers: { 'Content-Type': 'application/json' }
    });

    return response;
};

export const postGatewayProcessing = async ({ url, payload }) => {
    const response = await fetchAxiosApi(url, {
        method: 'post',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'text'
    });

    return response;
};

export const fetchIntelliBoostXml = async () => {
    const response = await fetchAxiosApi(
        'https://aws.assets.clickandpledge.com/global/intellibooster/Buttons_Recurring.xml',
        { method: 'get',
            responseType: 'text' }
    );

    return response;
};

/**
 * Preload all essential form data during authentication.
 * Fetches campaign raised, recurring options, and payment details in parallel.
 * Only binds campaign raised data immediately; other data needs processing in components.
 * @param {string} guid - Form GUID
 * @param {number} giventId - Givent ID
 * @param {string} organizationId - Organization ID
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>} Object containing all preloaded data
 */
export const preloadFormData = ({ guid, giventId }) => async dispatch => {
    try {
        const results = await Promise.allSettled([
            dispatch(fetchRecurringDataCached({ guid,
                giventId })),
            dispatch(fetchPaymentDetailsCached({ guid,
                giventId }))
        ]);

        return {
            recurringData: results[0].status === 'fulfilled' ? results[0].value : null,
            paymentDetails: results[1].status === 'fulfilled' ? results[1].value : null,
            errors: results.filter(r => r.status === 'rejected').map(r => r.reason)
        };
    } catch (error) {
        return {
            recurringData: null,
            paymentDetails: null,
            errors: [ error ]
        };
    }
};
